import {
  computePasteNumberPlan,
  rewriteNodeNumberReferences,
} from './paste-node-numbering';

/**
 * Turn a Resource Library item's catalogue rows into rows that are safe to
 * insert into a solution — shared by every insert path (Table drop,
 * click-to-add, canvas drop).
 *
 * - The catalogue's own `id` is dropped: the store mints a fresh one, so the
 *   same item added twice never collides on id.
 * - The catalogue's `order` (always 0) is dropped: the insert computes the
 *   real position, so a Table drop no longer sorts to the top.
 * - Group rows are skipped: groups are one level deep, and each insert path
 *   decides its own grouping.
 * - Node numbers follow the "Smart" paste policy (PSTUDIO-3602): kept when
 *   they're free, otherwise the whole item is renumbered past the solution's
 *   max and its own `nextNodes` / `whatsNext` / asset `dest` references are
 *   rewritten to match, so the inserted nodes still route to each other.
 *
 * Pure: the caller passes the numbers already in use and the current max.
 */
export function prepareResourceLibRows<
  T extends {
    id?: unknown;
    order?: unknown;
    nodeType?: unknown;
    nodeNumber?: unknown;
    nextNodes?: unknown;
    whatsNext?: unknown;
    richAssetContent?: unknown;
  },
>(
  catalogueRows: readonly T[],
  opts: {
    occupiedNumbers: ReadonlySet<number>;
    /** Where a renumbered item's block starts — the node the rows go after
     *  (PSTUDIO-3992's numbering context). The store passes the highest node
     *  number, so a colliding item is renumbered past the max. */
    reference: number | null;
    isGroupRow: (row: T) => boolean;
  }
): Array<Omit<T, 'id' | 'order'>> {
  const nodes = catalogueRows.filter((row) => !opts.isGroupRow(row));

  const plan = computePasteNumberPlan({
    bundle: nodes.map((row) => ({
      nodeNumber: row.nodeNumber,
      isGroupHeader: false,
    })),
    occupiedNumbers: opts.occupiedNumbers,
    reference: opts.reference,
    upper: null,
    autoRenumber: true,
  });

  return nodes.map((row) => {
    // Deep-copy so the reference rewrite (which mutates in place) never
    // touches the catalogue. JSON, not structuredClone: a Table drop hands
    // over MobX-observable rows (the drag data lives in tableStore), which
    // structuredClone refuses to copy. Catalogue rows are plain JSON data.
    const {
      id: _id,
      order: _order,
      ...rest
    } = JSON.parse(JSON.stringify(row)) as T;
    const prepared = rest as Omit<T, 'id' | 'order'> & {
      nodeNumber?: unknown;
    };
    if (plan.mode === 'renumber') {
      const mapped = plan.numberMap.get(Number(row.nodeNumber));
      if (mapped !== undefined) prepared.nodeNumber = mapped;
      rewriteNodeNumberReferences(prepared, plan.numberMap);
    }
    return prepared;
  });
}
